const SITE_URL = 'https://aitoolly.com';

export const AI_NEWS_RSS_LOCALES = [
  'en',
  'zh',
  'de',
] as const;

export type AiNewsRssLocale = (typeof AI_NEWS_RSS_LOCALES)[number];

export interface AiNewsRssItem {
  id: string;
  slug?: string | null;
  date: string;
  title: string;
  summary?: string | null;
  category?: string | null;
  tags?: string[] | null;
  image_url?: string | null;
  published_at?: string | null;
  updated_at?: string | null;
  source_name?: string | null;
  source_url?: string | null;
}

interface BuildAiNewsRssOptions {
  locale: AiNewsRssLocale;
  items: AiNewsRssItem[];
  generatedAt?: Date;
}

const feedCopy: Record<string, { title: string; description: string; language: string; readMore: string; originalSource: string }> = {
  en: {
    title: 'AIToolly AI News',
    description: 'Daily curated artificial intelligence news, product launches, research, and industry updates from AIToolly.',
    language: 'en-US',
    readMore: 'Read the full article on AIToolly',
    originalSource: 'Original source',
  },
  zh: {
    title: 'AIToolly AI 新闻',
    description: 'AIToolly 每日精选人工智能新闻、产品发布、研究进展与行业动态。',
    language: 'zh-CN',
    readMore: '在 AIToolly 阅读全文',
    originalSource: '原始来源',
  },
  de: {
    title: 'AIToolly KI-News',
    description: 'Täglich kuratierte KI-News zu Forschung, Produkten, Unternehmen und Entwicklungen der künstlichen Intelligenz.',
    language: 'de-DE',
    readMore: 'Vollständige Analyse auf AIToolly lesen',
    originalSource: 'Originalquelle',
  },
};

export function isAiNewsRssLocale(value: string): value is AiNewsRssLocale {
  return (AI_NEWS_RSS_LOCALES as readonly string[]).includes(value);
}

export function aiNewsRssPath(locale: string) {
  return locale === 'en' ? '/ai-news/rss.xml' : `/${locale}/ai-news/rss.xml`;
}

export function aiNewsPath(locale: string) {
  return locale === 'en' ? '/ai-news' : `/${locale}/ai-news`;
}

export function aiNewsArchivePath(locale: string, date: string) {
  return `${aiNewsPath(locale)}/${encodeURIComponent(date)}`;
}

export function aiNewsArticlePath(locale: string, identifier: string) {
  return locale === 'en'
    ? `/ai-news/article/${encodeURIComponent(identifier)}`
    : `/${locale}/ai-news/article/${encodeURIComponent(identifier)}`;
}

function articlePath(locale: string, item: AiNewsRssItem) {
  return aiNewsArticlePath(locale, item.slug || item.id);
}

function escapeXml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cdata(value: string) {
  return `<![CDATA[${value.replace(/]]>/g, ']]]]><![CDATA[>')}]]>`;
}

function decodeHtmlEntities(value: string) {
  return value
    .replace(/&#x([0-9a-f]+);?/gi, (_, code) => String.fromCodePoint(Number.parseInt(code, 16)))
    .replace(/&#([0-9]+);?/g, (_, code) => String.fromCodePoint(Number.parseInt(code, 10)))
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'");
}

function normalizedHttpUrl(value?: string | null) {
  if (!value) return null;
  const decoded = decodeHtmlEntities(value.trim());

  try {
    const url = new URL(decoded);
    return url.protocol === 'http:' || url.protocol === 'https:' ? decoded : null;
  } catch {
    return null;
  }
}

function validDate(value?: string | null) {
  if (!value) return null;
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function itemDate(item: AiNewsRssItem, fallback: Date) {
  return validDate(item.published_at) || validDate(item.date) || fallback;
}

function lastBuildDate(items: AiNewsRssItem[], fallback: Date) {
  const timestamps = items.flatMap((item) => [
    validDate(item.updated_at)?.getTime(),
    validDate(item.published_at)?.getTime(),
    validDate(item.date)?.getTime(),
  ]).filter((value): value is number => typeof value === 'number');

  return timestamps.length > 0 ? new Date(Math.max(...timestamps)) : fallback;
}

function itemCategories(item: AiNewsRssItem) {
  return Array.from(new Set([item.category, ...(item.tags || [])].filter(Boolean)))
    .map((category) => `      <category>${escapeXml(category)}</category>`)
    .join('\n');
}

function itemContent(item: AiNewsRssItem, articleUrl: string, copy: typeof feedCopy.en) {
  const summary = item.summary?.trim() || item.title;
  const sourceLink = item.source_url
    ? `<p><a href="${escapeHtml(item.source_url)}">${escapeHtml(copy.originalSource)}${item.source_name ? `: ${escapeHtml(item.source_name)}` : ''}</a></p>`
    : '';

  return [
    `<p>${escapeHtml(summary).replace(/\n/g, '<br />')}</p>`,
    `<p><a href="${escapeHtml(articleUrl)}">${escapeHtml(copy.readMore)}</a></p>`,
    sourceLink,
  ].filter(Boolean).join('');
}

export function buildAiNewsRss({ locale, items, generatedAt = new Date() }: BuildAiNewsRssOptions) {
  const copy = feedCopy[locale] || feedCopy.en;
  const feedUrl = `${SITE_URL}${aiNewsRssPath(locale)}`;
  const channelUrl = `${SITE_URL}${aiNewsPath(locale)}`;
  const buildDate = lastBuildDate(items, generatedAt).toUTCString();

  const rssItems = items.map((item) => {
    const articleUrl = `${SITE_URL}${articlePath(locale, item)}`;
    const categories = itemCategories(item);
    const imageUrl = normalizedHttpUrl(item.image_url);
    const image = imageUrl
      ? `\n      <media:content url="${escapeXml(imageUrl)}" medium="image" />\n      <media:thumbnail url="${escapeXml(imageUrl)}" />`
      : '';

    return `    <item>
      <title>${escapeXml(item.title)}</title>
      <link>${escapeXml(articleUrl)}</link>
      <guid isPermaLink="true">${escapeXml(articleUrl)}</guid>
      <description>${escapeXml(item.summary?.trim() || item.title)}</description>
      <content:encoded>${cdata(itemContent(item, articleUrl, copy))}</content:encoded>
      <pubDate>${itemDate(item, generatedAt).toUTCString()}</pubDate>
      <dc:creator>AIToolly</dc:creator>${categories ? `\n${categories}` : ''}${image}
    </item>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0"
  xmlns:atom="http://www.w3.org/2005/Atom"
  xmlns:content="http://purl.org/rss/1.0/modules/content/"
  xmlns:dc="http://purl.org/dc/elements/1.1/"
  xmlns:media="http://search.yahoo.com/mrss/">
  <channel>
    <title>${escapeXml(copy.title)}</title>
    <link>${escapeXml(channelUrl)}</link>
    <description>${escapeXml(copy.description)}</description>
    <language>${escapeXml(copy.language)}</language>
    <atom:link href="${escapeXml(feedUrl)}" rel="self" type="application/rss+xml" />
    <lastBuildDate>${buildDate}</lastBuildDate>
    <generator>AIToolly</generator>
    <ttl>30</ttl>
    <image>
      <url>${SITE_URL}/icon.png</url>
      <title>${escapeXml(copy.title)}</title>
      <link>${escapeXml(channelUrl)}</link>
    </image>
${rssItems}
  </channel>
</rss>
`;
}
